import { default as classNames, default as classnames } from "classnames";
import React, { useEffect, useState } from "react";
import flags from "../../utils/flags";
import { useIntl } from "react-intl";
import { Alternate } from "../../model/language";
import {
  ToolbarChildItem,
  ToolbarItem,
  ToolbarRootItem
} from "../../model/menu";
import { LoginStatus, useAuth } from "../../utils/AuthContext";
import ToolBarNavDivider from "../Toolbar/ToolBarNavDivider/ToolBarNavDivider";
import LanguageList from "./LanguageList";
import NavLink from "./NavLink";
import styles from "./ToolbarRightNav.module.scss";
import { useIsRtl } from "../../utils/LayoutContext";
import Text from "../Text/Text";
import ChevronRightIcon from "../../icons/ChevronRightIcon";
import ChevronLeftIcon from "../../icons/ChevronLeftIcon";

interface ToolbarRightNavProps {
  alternates: Alternate[];
  navItems: ToolbarRootItem[];
  showLanguage: boolean;
  setShowLanguage: (showLanguage: boolean) => void;
  firstLevelMatch: ToolbarItem | undefined;
  secondLevelMatch: ToolbarItem | undefined;
  thirdLevelMatch: ToolbarItem | undefined;
}

// This has a mobile and normal mode.
const ToolbarRightNav = ({
  alternates,
  navItems,
  showLanguage,
  setShowLanguage,
  firstLevelMatch,
  secondLevelMatch,
  thirdLevelMatch
}: ToolbarRightNavProps) => {
  const [expandedItem, setExpandedItem] = useState<ToolbarItem | null>(
    firstLevelMatch &&
      firstLevelMatch.childLinks &&
      firstLevelMatch.childLinks.length
      ? firstLevelMatch
      : null
  );
  const {
    userInfo: { status }
  } = useAuth();
  const redirect = new URLSearchParams({
    redirect:
      typeof window !== "undefined"
        ? window.location.href
        : "https://microbit.org/"
  });
  const intl = useIntl();
  const languageString = intl.formatMessage({ id: "language" });
  const helpAndSupportString = intl.formatMessage({ id: "help-and-support" });
  const safetyString = intl.formatMessage({ id: "safety", defaultMessage: "Safety" });
  const isRtl = useIsRtl();
  return (
    <div className={styles.root}>
      {showLanguage && (
        <LanguageList
          variant="toolbarDefaultLight"
          className={classnames(
            styles.languages,
            styles.showMobileAndTabletOnly
          )}
          alternates={alternates}
          setShowLanguage={setShowLanguage}
        />
      )}
      <ul
        className={classnames(styles.navContainer, {
          [styles.hideMobileAndTablet]: showLanguage
        })}
        id="main-menu"
        role="menu"
        aria-labelledby="main-menu-button"
      >
        {expandedItem && (
          <MainMenuButton onClick={() => setExpandedItem(null)} />
        )}
        {navItems.map(navItem => {
          return (
            <React.Fragment key={navItem._key}>
              <li
                key={navItem._key}
                role="presentation"
                className={classNames(
                  styles.row,
                  styles.showDesktopOnly,
                  styles.primaryLinkCol
                )}
              >
                {navItem.internalLink && (
                  <div className={styles.titleContainer} role="presentation">
                    <NavLink
                      data={navItem.internalLink}
                      className={classnames(
                        firstLevelMatch &&
                          navItem._key === firstLevelMatch._key &&
                          styles.parentActive
                      )}
                      role="menuitem"
                    >
                      <Text
                        data-content={navItem.title}
                        variant="toolbarDefault"
                        as="p"
                      >
                        {navItem.title}
                      </Text>
                    </NavLink>
                  </div>
                )}
              </li>

              <MobileNavItem
                rootMatch={firstLevelMatch as ToolbarRootItem}
                navItem={navItem}
                setExpandedItem={setExpandedItem}
                expandedItem={expandedItem}
                secondLevelMatch={secondLevelMatch}
                thirdLevelMatch={thirdLevelMatch}
                isRtl={isRtl}
              />

              {navItem.showDivider && (
                <div className={styles.showDesktopOnly}>
                  <ToolBarNavDivider
                    className={classNames(
                      styles.divider,
                      styles.showDesktopOnly
                    )}
                  />
                </div>
              )}

              {navItem.showDivider && !expandedItem && (
                <div className={styles.showMobileAndTabletOnly}>
                  <ToolBarNavDivider
                    className={classNames(
                      styles.divider,
                      styles.showMobileAndTabletOnly
                    )}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}

        {flags.loginDemo && (
          <div
            key="auth"
            className={styles.row}
            style={{
              visibility: status === LoginStatus.TBD ? "hidden" : "unset"
            }}
            aria-hidden={status === LoginStatus.TBD}
          >
            <div className={styles.primaryLinkCol}>
              <div className={styles.titleContainer}>
                <a
                  href={
                    status === LoginStatus.TBD
                      ? undefined
                      : status === LoginStatus.LOGGED_IN
                      ? `/api/v1/accounts/logout?${redirect}`
                      : `/api/v1/google/login?${redirect}`
                  }
                  className={classnames(
                    firstLevelMatch &&
                      "auth" === firstLevelMatch._key &&
                      styles.parentActive
                  )}
                >
                  <Text variant="toolbarDefault" as="p">
                    {status === LoginStatus.LOGGED_IN ? "Logout" : "Login"}
                  </Text>
                </a>
              </div>
            </div>
          </div>
        )}

        {!expandedItem && (
          <div className={styles.showMobileAndTabletOnly}>
            <ToolBarNavDivider className={styles.divider} />
            <div className={styles.minorNav}>
              <li className={styles.secondaryLinkCol}>
                <button
                  onClick={e => {
                    e.preventDefault();
                    setShowLanguage(!showLanguage);
                  }}
                >
                  <Text
                    data-content={languageString}
                    variant="toolbarDefaultLight"
                  >
                    {languageString}
                  </Text>
                </button>
              </li>
              <li className={styles.secondaryLinkCol}>
                <NavLink href="https://support.microbit.org">
                  <Text
                    data-content={helpAndSupportString}
                    variant="toolbarDefaultLight"
                  >
                    {helpAndSupportString}
                  </Text>
                </NavLink>
              </li>
              <NavLink href="/get-started/user-guide/safety/">
                <Text data-content={safetyString} variant="toolbarDefaultLight">
                  {safetyString}
                </Text>
              </NavLink>
            </div>
          </div>
        )}
      </ul>
    </div>
  );
};

interface MobileNavItemProps {
  navItem: ToolbarItem;
  secondLevelMatch?: ToolbarItem;
  thirdLevelMatch?: ToolbarItem;
  rootMatch: ToolbarRootItem;
  setExpandedItem: (expandedItem: ToolbarItem | null) => void;
  expandedItem: ToolbarItem | null;
  isRtl: boolean;
}

const MobileNavItem = ({
  navItem,
  setExpandedItem,
  expandedItem,
  rootMatch,
  secondLevelMatch,
  thirdLevelMatch,
  isRtl
}: MobileNavItemProps) => {
  if (expandedItem && expandedItem !== navItem) {
    return null;
  }
  return (
    <li
      key={navItem._key}
      className={classNames(styles.row, styles.showMobileAndTabletOnly)}
      role="presentation"
    >
      <div className={styles.primaryLinkCol} role="presentation">
        {navItem.internalLink && (
          <div className={styles.titleContainer} role="presentation">
            {navItem.childLinks && navItem.childLinks.length > 0 ? (
              <button
                id={`${navItem.title}-menubutton`}
                aria-haspopup="true"
                aria-controls={`${navItem.title}-menu`}
                role="menuitem"
                onClick={() =>
                  setExpandedItem(expandedItem === navItem ? null : navItem)
                }
              >
                <Text
                  data-content={navItem.title}
                  className={classNames(
                    rootMatch &&
                      navItem._key === rootMatch._key &&
                      styles.parentActive
                  )}
                  variant="toolbarDefault"
                  as="p"
                >
                  {navItem.title}
                </Text>
                <ChevronRightIcon
                  stroke="#cd0365"
                  className={classnames(
                    styles.chevronIcon,
                    expandedItem === navItem && styles.rotateIcon,
                    isRtl && styles.flipIcon
                  )}
                />
              </button>
            ) : (
              <NavLink
                data={navItem.internalLink}
                className={classnames(
                  rootMatch &&
                    navItem._key === rootMatch._key &&
                    styles.parentActive
                )}
                role="menuitem"
              >
                <Text
                  data-content={navItem.title}
                  variant="toolbarDefault"
                  as="p"
                >
                  {navItem.title}
                </Text>
              </NavLink>
            )}
          </div>
        )}

        {/* Second level nav */}
        {expandedItem && expandedItem === navItem && navItem.childLinks && (
          <ul
            className={styles.secondaryLinksRow}
            id={`${navItem.title}-menu`}
            role="menu"
            aria-labelledby={`${navItem.title}-menubutton`}
          >
            {navItem.childLinks.map((secondaryNavItem: ToolbarChildItem) => (
              <SecondaryMobileNavItem
                key={secondaryNavItem._key}
                navItem={secondaryNavItem}
                expandedItem={expandedItem}
                secondLevelMatch={secondLevelMatch}
                thirdLevelMatch={thirdLevelMatch}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
};

interface SecondaryMobileNavItemProps {
  navItem: ToolbarItem;
  secondLevelMatch?: ToolbarItem;
  thirdLevelMatch?: ToolbarItem;
  expandedItem: ToolbarItem | null;
}

const SecondaryMobileNavItem = ({
  navItem,
  expandedItem,
  secondLevelMatch,
  thirdLevelMatch
}: SecondaryMobileNavItemProps) => {
  const [expandedMenu, setExpandedMenu] = useState<boolean>(
    Boolean(thirdLevelMatch && navItem.childLinks?.includes(thirdLevelMatch))
  );

  useEffect(() => {
    if (!expandedItem) {
      setExpandedMenu(false);
    }
  }, [expandedItem]);

  const isRtl = useIsRtl();
  return (
    <li
      key={navItem._key}
      className={styles.secondaryLinkCol}
      role="presentation"
    >
      {navItem.childLinks && navItem.childLinks.length > 0 ? (
        <>
          <button
            id={`${navItem.title}-menubutton`}
            aria-haspopup="true"
            aria-controls={`${navItem.title}-menu`}
            role="menuitem"
            onClick={() => setExpandedMenu(!expandedMenu)}
          >
            <Text
              data-content={navItem.title}
              className={classNames(
                thirdLevelMatch &&
                  navItem.childLinks.includes(thirdLevelMatch) &&
                  styles.parentActive
              )}
              variant="toolbarDefaultLight"
              as="p"
            >
              {navItem.title}
            </Text>
            <ChevronRightIcon
              stroke="#cd0365"
              className={classnames(
                styles.chevronIcon,
                expandedMenu && styles.rotateIcon,
                isRtl && styles.flipIcon
              )}
            />
          </button>
          {/* Third level nav */}
          {expandedMenu && navItem.childLinks && (
            <ul
              className={styles.tertiaryLinksRow}
              id={`${navItem.title}-menu`}
              role="menu"
              aria-labelledby={`${navItem.title}-menubutton`}
            >
              {navItem.childLinks.map((tertiaryNavItem: ToolbarChildItem) => (
                <li
                  key={tertiaryNavItem._key}
                  className={styles.tertiaryLinkCol}
                  role="presentation"
                >
                  <NavLink
                    className={classnames(
                      thirdLevelMatch &&
                        tertiaryNavItem._key === thirdLevelMatch._key &&
                        styles.parentActive
                    )}
                    key={tertiaryNavItem._key}
                    data={tertiaryNavItem.internalLink}
                    role="menuitem"
                  >
                    <Text
                      data-content={tertiaryNavItem.title}
                      variant="toolbarDefaultLight"
                    >
                      {tertiaryNavItem.title}
                    </Text>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <NavLink
          key={navItem._key}
          data={navItem.internalLink}
          className={classnames(
            secondLevelMatch &&
              navItem._key === secondLevelMatch._key &&
              styles.parentActive
          )}
          role="menuitem"
        >
          <Text data-content={navItem.title} variant="toolbarDefaultLight">
            {navItem.title}
          </Text>
        </NavLink>
      )}
    </li>
  );
};

export const MainMenuButton = ({ onClick }: { onClick: () => void }) => {
  const intl = useIntl();
  const string = intl.formatMessage({ id: "main-menu", defaultMessage: "Main menu" });
  const isRtl = useIsRtl();
  return (
    <>
      <li
        className={classnames(
          styles.showMobileAndTabletOnly,
          styles.mainMenuLi
        )}
        role="presentation"
      >
        <button
          className={classnames(styles.mainMenuButton)}
          onClick={onClick}
          role="menuitem"
        >
          <ChevronLeftIcon
            stroke="#cd0365"
            className={classNames(styles.chevronIcon, isRtl && styles.flipIcon)}
          />
          <Text data-content={string} variant="toolbarDefault" as="p">
            {string}
          </Text>
        </button>
      </li>
      <ToolBarNavDivider
        className={classNames(styles.divider, styles.showMobileAndTabletOnly)}
      />
    </>
  );
};

export default ToolbarRightNav;
